import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button, Card, Badge, Spinner, fmtNum } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";
import { Plus, Truck, Search, ArrowLeft, Trash2, Pencil, X } from "lucide-react";

const CAT_LABELS = { cx1: "CX1", cx2: "CX2", cx3: "CX3", cx3g: "CX3G", cx4: "CX4" };

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso.length <= 10 ? iso + "T00:00:00" : iso);
  if (isNaN(d.getTime())) return iso;
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

export default function TerceirosRetiradas() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // retirada em edição
  const [savingEdit, setSavingEdit] = useState(false);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const params = {};
      if (q) params.q = q;
      const { data } = await api.get("/terceiros/retiradas", { params });
      setItems(data || []);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onDelete = async (rid, num) => {
    if (!window.confirm(`Cancelar retirada ${num}? Os pallets voltarão ao estoque de terceiros.`)) return;
    try {
      await api.delete(`/terceiros/retiradas/${rid}`);
      await load();
    } catch (e) { alert(formatErr(e)); }
  };

  const openEdit = (r) => {
    setEditing({
      id: r.id,
      numero: r.numero,
      data_retirada: (r.data_retirada || "").slice(0, 10),
      vendedor: r.vendedor || "",
      motorista: r.motorista || "",
      placa: r.placa || "",
      observacoes: r.observacoes || "",
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSavingEdit(true);
    try {
      const { id, numero, ...body } = editing; // eslint-disable-line no-unused-vars
      await api.patch(`/terceiros/retiradas/${id}`, body);
      setEditing(null);
      await load();
    } catch (e) { alert(formatErr(e)); }
    finally { setSavingEdit(false); }
  };

  return (
    <Layout
      title="Retiradas / Devoluções de Terceiros"
      subtitle="Controle físico de entregas dos pallets aos clientes donos"
      actions={
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => nav("/terceiros/pallets")}><ArrowLeft size={16} /> Voltar</Button>
          <Link to="/terceiros/retiradas/new">
            <Button className="!bg-purple-600 hover:!bg-purple-700" data-testid="new-retirada-btn"><Plus size={16} /> Nova Retirada</Button>
          </Link>
        </div>
      }
    >
      <Card className="p-4 mb-4">
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1.5">Buscar</div>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-300" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                placeholder="Número, cliente, motorista..."
                className="w-full rounded-md bg-stone-50 border border-stone-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                data-testid="retiradas-search"
              />
            </div>
          </div>
          <Button onClick={load} className="!bg-purple-600 hover:!bg-purple-700"><Search size={14} /> Buscar</Button>
        </div>
      </Card>

      {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md mb-3">{err}</div>}

      <Card>
        {loading ? (
          <div className="p-8 text-center"><Spinner size={20} /></div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-ink-500">
            <Truck size={32} className="mx-auto mb-2 text-ink-300" />
            <div>Nenhuma retirada registrada.</div>
            <Link to="/terceiros/retiradas/new" className="text-purple-600 hover:underline text-sm mt-2 inline-block">Registrar primeira retirada →</Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-ink-200">
                <tr className="text-xs font-bold uppercase tracking-wider text-ink-500">
                  <th className="px-4 py-3 text-left">Retirada</th>
                  <th className="px-4 py-3 text-left">Cliente</th>
                  <th className="px-4 py-3 text-left">Data</th>
                  <th className="px-4 py-3 text-right">Pallets</th>
                  <th className="px-4 py-3 text-right">Sacos</th>
                  <th className="px-4 py-3 text-left">Por Categoria</th>
                  <th className="px-4 py-3 text-left">Vendedor</th>
                  <th className="px-4 py-3 text-left">Motorista</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-b border-ink-200 hover:bg-purple-50/30" data-testid={`retirada-row-${r.id}`}>
                    <td className="px-4 py-3 font-mono font-bold text-purple-800">{r.numero}</td>
                    <td className="px-4 py-3 font-semibold text-purple-900">{r.cliente_dono}</td>
                    <td className="px-4 py-3 font-mono text-xs">{fmtDate(r.data_retirada)}</td>
                    <td className="px-4 py-3 text-right font-mono">{r.total_pallets}</td>
                    <td className="px-4 py-3 text-right font-mono font-bold">{fmtNum(r.total_sacos, 0)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(r.por_categoria || {}).filter(([, v]) => v > 0).map(([k, v]) => (
                          <Badge key={k} tone="neutral" className="!bg-purple-100 !text-purple-700 !border-purple-200 text-[10px]">
                            {CAT_LABELS[k]}: {fmtNum(v, 0)}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs">{r.vendedor || <span className="text-ink-400">—</span>}</td>
                    <td className="px-4 py-3 text-xs">{r.motorista || "—"} <span className="text-ink-400 font-mono">{r.placa}</span></td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openEdit(r)} className="p-2 rounded-md hover:bg-brand-50 text-brand-700" title="Editar dados da retirada" data-testid={`edit-retirada-${r.id}`}>
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => onDelete(r.id, r.numero)} className="p-2 rounded-md hover:bg-red-50 text-danger" title="Cancelar retirada" data-testid={`delete-retirada-${r.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !savingEdit && setEditing(null)} data-testid="edit-retirada-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-ink-200">
              <h3 className="font-heading text-lg font-bold text-ink-900">Editar Retirada {editing.numero}</h3>
              <button onClick={() => setEditing(null)} className="p-1 rounded hover:bg-stone-100 text-ink-600"><X size={16} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-ink-500 mb-1">Data da retirada</label>
                <input
                  type="date"
                  value={editing.data_retirada}
                  onChange={(e) => setEditing({ ...editing, data_retirada: e.target.value })}
                  className="w-full h-10 rounded border border-ink-200 px-3 focus:ring-2 focus:ring-brand-500 outline-none"
                  data-testid="edit-retirada-data"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-ink-500 mb-1">Vendedor</label>
                <input
                  type="text"
                  value={editing.vendedor}
                  onChange={(e) => setEditing({ ...editing, vendedor: e.target.value })}
                  className="w-full h-10 rounded border border-ink-200 px-3 focus:ring-2 focus:ring-brand-500 outline-none"
                  placeholder="Ex.: DIRCEU"
                  data-testid="edit-retirada-vendedor"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-ink-500 mb-1">Motorista</label>
                  <input
                    type="text"
                    value={editing.motorista}
                    onChange={(e) => setEditing({ ...editing, motorista: e.target.value })}
                    className="w-full h-10 rounded border border-ink-200 px-3 focus:ring-2 focus:ring-brand-500 outline-none"
                    data-testid="edit-retirada-motorista"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-ink-500 mb-1">Placa</label>
                  <input
                    type="text"
                    value={editing.placa}
                    onChange={(e) => setEditing({ ...editing, placa: e.target.value.toUpperCase() })}
                    className="w-full h-10 rounded border border-ink-200 px-3 font-mono focus:ring-2 focus:ring-brand-500 outline-none"
                    data-testid="edit-retirada-placa"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-ink-500 mb-1">Observações</label>
                <textarea
                  value={editing.observacoes}
                  onChange={(e) => setEditing({ ...editing, observacoes: e.target.value })}
                  rows={3}
                  className="w-full rounded border border-ink-200 px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                  data-testid="edit-retirada-obs"
                />
              </div>
              <div className="text-[11px] text-ink-500 bg-stone-50 border border-ink-100 rounded p-2">
                <strong>Nota:</strong> aqui você edita apenas dados cadastrais. Para alterar quantidades ou pallets, exclua e recadastre a retirada.
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-ink-200 bg-stone-50">
              <Button variant="secondary" onClick={() => setEditing(null)} disabled={savingEdit} data-testid="edit-retirada-cancel">Cancelar</Button>
              <Button onClick={saveEdit} disabled={savingEdit} data-testid="edit-retirada-save">
                {savingEdit ? "Salvando..." : "Salvar alterações"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
